import {
  isListTheme,
  LIST_TEMPLATES,
  LIST_THEMES,
  TEMPLATE_CONFIG,
  type ListTemplate,
  type ListTheme,
} from "@listou/shared/constants";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ListPage } from "../components/list-page";
import { PaletteSwatch } from "../components/palette-swatch";
import { useToast } from "../components/toast";
import { DEMO_LISTS, OCCASION_DEMOS } from "../lib/demo";
import { seo } from "../lib/head";
import type { LandingOccasion } from "../lib/occasion-pages";
import { themeLabel } from "../lib/theme";

export const Route = createFileRoute("/exemple")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { modele?: ListTemplate; palette?: ListTheme; occasion?: LandingOccasion } => ({
    occasion:
      typeof search.occasion === "string" && search.occasion in OCCASION_DEMOS
        ? (search.occasion as LandingOccasion)
        : undefined,
    modele: LIST_TEMPLATES.includes(search.modele as ListTemplate)
      ? (search.modele as ListTemplate)
      : undefined,
    palette:
      typeof search.palette === "string" && isListTheme(search.palette)
        ? search.palette
        : undefined,
  }),
  staticData: { ssr: true },
  head: () =>
    seo({
      title: "Exemples de listes d'envies · Listou",
      description:
        "Une liste de naissance, de Noël, d'anniversaire ou de mariage telle que vos proches la verront, dans chaque modèle et chaque palette.",
    }),
  component: ExamplePage,
});

/** A sample list in each template, so a visitor sees the real page before signing up. */
function ExamplePage() {
  const search = Route.useSearch();
  const { palette, occasion } = search;
  // An occasion example keeps its own list and look; the template tabs restyle it.
  const occasionDemo = occasion ? OCCASION_DEMOS[occasion] : null;
  const modele = search.modele ?? occasionDemo?.list.appearance.template ?? "pop";
  const toast = useToast();
  const base = occasionDemo
    ? {
        ...occasionDemo,
        list: {
          ...occasionDemo.list,
          appearance: { ...occasionDemo.list.appearance, template: modele },
        },
      }
    : DEMO_LISTS[modele];
  // Any palette on any template: the example is also how a visitor compares looks.
  const demo = palette
    ? { ...base, list: { ...base.list, theme: palette, accentColor: null } }
    : base;

  return (
    <div className="min-h-dvh">
      <div className="sticky top-0 z-30 border-b-2 border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <p className="hidden font-bold sm:block">Exemple de liste</p>
          <nav
            className="-mx-1 order-last flex w-full gap-1 overflow-x-auto sm:order-none sm:w-auto"
            aria-label="Modèles"
          >
            {LIST_TEMPLATES.map((template) => (
              <Link
                key={template}
                to="/exemple"
                search={{ modele: template, palette, occasion }}
                aria-current={template === modele ? "page" : undefined}
                className={`shrink-0 rounded-full px-4 py-2 font-bold transition ${
                  template === modele ? "bg-ink text-surface" : "hover:bg-ink/[0.06]"
                }`}
              >
                {TEMPLATE_CONFIG[template].label}
              </Link>
            ))}
          </nav>
          <Link to="/nouvelle-liste" className="btn btn-sm">
            Créer la mienne
          </Link>
        </div>
      </div>
      <nav
        aria-label="Palettes"
        className="flex gap-2 overflow-x-auto border-b-2 border-line bg-surface px-4 py-2 sm:justify-center"
      >
        {LIST_THEMES.map((theme) => (
          <Link
            key={theme}
            to="/exemple"
            search={{ modele, palette: theme, occasion }}
            aria-label={themeLabel(theme)}
            aria-current={(palette ?? base.list.theme) === theme ? "true" : undefined}
            title={themeLabel(theme)}
            className="size-9 shrink-0 rounded-full p-0.5 ring-offset-2 ring-offset-surface aria-[current]:ring-3 aria-[current]:ring-ink"
          >
            <PaletteSwatch theme={theme} />
          </Link>
        ))}
      </nav>
      <ListPage
        list={demo.list}
        wishes={demo.wishes}
        onReserve={() => toast("Sur une vraie liste, ce bouton réserve le cadeau pour vous.")}
      />
    </div>
  );
}
